import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { MultiSelectDropdown } from "../ui/multi-select-dropdown";
import { Loader2 } from "lucide-react";

interface InformationLibraryFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

interface Workspace {
  name: string;
  prefix: string;
  area: "Planning" | "Execution" | "ConcludingProcedures";
}

export function InformationLibraryFormModal({
  isOpen,
  onClose,
  onCreated,
}: InformationLibraryFormModalProps) {
  const [informationName, setInformationName] = useState("");
  const [selectedWorkspaces, setSelectedWorkspaces] = useState<string[]>([]);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [loadingWorkspaces, setLoadingWorkspaces] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>("");

  // Load workspaces from Sections.json
  useEffect(() => {
    if (isOpen) {
      loadWorkspaces();
    }
  }, [isOpen]);

  const loadWorkspaces = async () => {
    setLoadingWorkspaces(true);
    setError("");
    try {
      if ((window as any).sharePointAPI?.downloadSectionsJson) {
        const result = await (window as any).sharePointAPI.downloadSectionsJson();
        if (result?.success && result?.data) {
          const allWorkspaces: Workspace[] = [];
          
          // Extract workspaces from all areas
          const areas: Array<"Planning" | "Execution" | "ConcludingProcedures"> = [
            "Planning",
            "Execution",
            "ConcludingProcedures",
          ];
          
          areas.forEach((area) => {
            if (result.data[area] && typeof result.data[area] === "object") {
              Object.entries(result.data[area]).forEach(([name, prefix]) => {
                allWorkspaces.push({
                  name: String(name),
                  prefix: String(prefix),
                  area,
                });
              });
            }
          });
          
          // Sort workspaces by name
          allWorkspaces.sort((a, b) => a.name.localeCompare(b.name));
          setWorkspaces(allWorkspaces);
        } else {
          setError("Failed to load workspaces");
        }
      } else {
        setError("SharePoint API not available");
      }
    } catch (err) {
      console.error("Error loading workspaces:", err);
      setError("Failed to load workspaces");
    } finally {
      setLoadingWorkspaces(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!informationName.trim()) {
      setError("Information name is required");
      return;
    }
    
    if (selectedWorkspaces.length === 0) {
      setError("Please select at least one workspace");
      return;
    }

    setSaving(true);
    setError("");

    try {
      if ((window as any).sharePointAPI?.addLibrariesInformation) {
        // Add information for each selected workspace
        const results = await Promise.all(
          selectedWorkspaces.map((workspaceName) =>
            (window as any).sharePointAPI.addLibrariesInformation({
              informationName: informationName.trim(),
              workspaceName: workspaceName,
            })
          )
        );

        // Check if all operations succeeded
        const allSuccess = results.every((result) => result?.success);
        const firstError = results.find((result) => !result?.success);

        if (allSuccess) {
          // Reset form
          setInformationName("");
          setSelectedWorkspaces([]);
          onCreated();
          onClose();
        } else {
          setError(firstError?.error || "Failed to create information for some workspaces");
        }
      } else {
        setError("SharePoint API not available");
      }
    } catch (err) {
      console.error("Error creating information:", err);
      setError(err instanceof Error ? err.message : "Failed to create information");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      setInformationName("");
      setSelectedWorkspaces([]);
      setError("");
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] bg-black/90 border-white/10 text-white">
        <DialogHeader>
          <DialogTitle>Create Information</DialogTitle>
          <DialogDescription className="text-white/60">
            Add a new information entry to the Information Library
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label htmlFor="information-name" className="text-sm font-medium text-white">
                Information Name *
              </label>
              <Input
                id="information-name"
                value={informationName}
                onChange={(e) => {
                  setInformationName(e.target.value);
                  setError("");
                }}
                placeholder="Enter information name"
                className="bg-black/20 border-white/10 text-white placeholder:text-white/40"
                disabled={saving}
                required
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="workspace" className="text-sm font-medium text-white">
                Workspace(s) *
              </label>
              {loadingWorkspaces ? (
                <div className="flex items-center gap-2 text-white/60">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Loading workspaces...</span>
                </div>
              ) : (
                <MultiSelectDropdown
                  options={workspaces.map((w) => w.name)}
                  value={selectedWorkspaces}
                  onChange={(value) => {
                    setSelectedWorkspaces(value);
                    setError("");
                  }}
                  placeholder="Select workspace(s)"
                  disabled={saving || workspaces.length === 0}
                  loading={loadingWorkspaces}
                  showSelectAll={true}
                  className="w-full"
                />
              )}
            </div>

            {error && (
              <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-md p-3">
                {error}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={saving}
              className="border-white/10 text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving || !informationName.trim() || selectedWorkspaces.length === 0}
              className="bg-blue-600 text-white hover:bg-blue-700"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
